import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import { Modal, Form, Button } from "react-bulma-components";
import { connect, useDispatch, useSelector } from "react-redux";
import { doUploadFile } from "./../../../actions/file-upload";
import { head } from "ramda";
import FileUploader from "../../../common/file-uploader/FileUploader";
import StarshopApi from "../../../api/starshop.api";
import { doSetNewImageData } from "../../../actions/starshop";

const StarshopUpdateMerchModal = ({ onClose, onConfirm, data }) => {
  const [name, setName] = useState(data.name);
  const [description, setDescription] = useState(data.description);
  const [stockAvailable, setStockAvailable] = useState(data.stockAvailable);
  const [cost, setCost] = useState(data.cost);
  const [isHidden, setIsHidden] = useState(data.hidden);
  const [errors, setErrors] = useState({});

  const dispatch = useDispatch();
  const imageDetails = useSelector((state) => state.starshop.newImageData);

  const validate = () => {
    let newErrors = {};

    if (!name || name.trim().length < 2) {
      newErrors.name = "Name is required (at least 2 characters).";
    }

    if (!imageDetails && (!data.allImages || data.allImages.length === 0)) {
      newErrors.image = "At least one image is required.";
    }

    if (!stockAvailable || isNaN(stockAvailable) || parseInt(stockAvailable) < 1) {
      newErrors.stockAvailable = "Stock must be a positive number.";
    }

    if (!cost || isNaN(cost) || parseInt(cost) < 1) {
      newErrors.cost = "Cost must be a positive number.";
    }

    const tagRegex = /<\/?[^>]+(>|$)/g;
    if (tagRegex.test(description)) {
      newErrors.description = "Description cannot contain HTML tags.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdate = () => {
    if (!validate()) return;

    onConfirm({
      name,
      description,
      stockAvailable: parseInt(stockAvailable),
      cost: parseInt(cost),
      hidden: isHidden,
      imageFileNames: imageDetails
        ? [{ id: imageDetails.id, type: imageDetails.type }]
        : [{ id: data.allImages[0].id, type: data.allImages[0].type }],
    });
  };

  const uploadCallback = useCallback(
    (upLoadData) => {
      const data = head(upLoadData);

      dispatch(
        doUploadFile({
          actions: {
            successAction: doSetNewImageData,
          },
          data: {
            data: data.data,
            type: data.type,
            fileName: data.fileName,
          },
          endPoint: StarshopApi.postAdminUploadImage,
        })
      );
    },
    [dispatch]
  );

  return (
    <Modal.Card>
      <Modal.Card.Head showClose={false}>
        <Modal.Card.Title>Update Merch</Modal.Card.Title>
      </Modal.Card.Head>
      <Modal.Card.Body>
        {/* Name Field */}
        <Form.Field>
          <Form.Label>Name:</Form.Label>
          <Form.Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={errors.name ? "is-danger" : ""}
            placeholder="Cool mug"
          />
          {errors.name && <p className="error-text">{errors.name}</p>}
        </Form.Field>

        {/* Description Field */}
        <Form.Field>
          <Form.Label>Description:</Form.Label>
          <Form.Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className={errors.description ? "is-danger" : ""}
            placeholder="You might want to give these to your friends..."
          />
          {errors.description && <p className="error-text">{errors.description}</p>}
        </Form.Field>

        {/* Image Field */}
        <Form.Field>
          <Form.Label>Image:</Form.Label>
          {!imageDetails && data.allImages.length > 0 && (
            <img
              className="is-centered"
              src={data.allImages[0].imageLinks.large}
              alt={data.name}
              style={{}}
            />
          )}
          {imageDetails && (
            <img
              className="is-centered"
              src={imageDetails.imageLinks.large}
              alt={imageDetails.id}
              style={{}}
            />
          )}
          <FileUploader onUpload={uploadCallback} />
          {errors.image && <p className="error-text">{errors.image}</p>}
        </Form.Field>

        {/* Stock Available */}
        <Form.Field>
          <Form.Label>Stock available:</Form.Label>
          <Form.Input
            type="number"
            value={stockAvailable}
            onChange={(e) => setStockAvailable(e.target.value)}
            className={errors.stockAvailable ? "is-danger" : ""}
          />
          {errors.stockAvailable && <p className="error-text">{errors.stockAvailable}</p>}
        </Form.Field>

        {/* Cost */}
        <Form.Field>
          <Form.Label>Cost:</Form.Label>
          <Form.Input
            type="number"
            value={cost}
            onChange={(e) => setCost(e.target.value)}
            className={errors.cost ? "is-danger" : ""}
          />
          {errors.cost && <p className="error-text">{errors.cost}</p>}
        </Form.Field>

        {/* Hidden Checkbox */}
        <Form.Field>
          <Form.Control>
            <Form.Checkbox checked={isHidden} onChange={(e) => setIsHidden(e.target.checked)}>
              <strong style={{ marginLeft: '8px' }}>Hidden</strong>
            </Form.Checkbox>
          </Form.Control>
        </Form.Field>

        <Form.Label>Are you sure?</Form.Label>
        <Form.Field kind="group">
          <Form.Control>
            <Button color="info" onClick={handleUpdate}>
              Update
            </Button>
          </Form.Control>
          <Form.Control>
            <Button color="" colorVariant="light" onClick={onClose}>
              Cancel
            </Button>
          </Form.Control>
        </Form.Field>
      </Modal.Card.Body>
    </Modal.Card>
  );
};

StarshopUpdateMerchModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => {
  return {};
};

const mapDispatchToProps = (dispatch) => {
  return {};
};

export default connect(mapStateToProps, mapDispatchToProps)(StarshopUpdateMerchModal);
